import {LEADERBOARD_FETCH_THROTTLING_TIME} from './LeaderBoardService.constants';
import {DI_TOKENS} from '@/di/di.tokens';
import {LeagueType} from '@/stores/LeaderBoardStore/LeaderBoardStore.constants';
import {LeagueProps} from '@/stores/LeaderBoardStore/LeaderBoardStore.types';
import throttle from 'lodash.throttle';

import {injected} from 'brandi';

import {HttpError} from '@specials/project-user';

import {ErrorsService} from '@/services/ErrorsService/ErrorsService';
import {LoggerService} from '@/services/LoggerService/LoggerService';
import {IProjectApi} from '@/services/ProjectApi/types';
import {GameStatusService} from '@/services/shared/GameStatusService/GameStatusService';
import {GameStatusStore} from '@/stores/GameStatusStore/GameStatusStore';
import {LeaderBoardStore} from '@/stores/LeaderBoardStore/LeaderBoardStore';
import {DateUtils} from '@/utils/DateUtils';
import {FormatUtils} from '@/utils/FormatUtils';

import {NavigationClickerModalService} from '../../NavigationClickerModalService/NavigationClickerModalService';
import {ProjectDate} from '../../ProjectDate/ProjectDate';

export class LeaderBoardService {
  constructor(
    private readonly projectApi: IProjectApi,
    private readonly leaderBoardStore: LeaderBoardStore,
    private readonly gameStatusStore: GameStatusStore,
    private readonly loggerService: LoggerService,
    private readonly errorsService: ErrorsService,
    private readonly gameStatusService: GameStatusService,
    private readonly navigationClickerModalService: NavigationClickerModalService,
    private readonly projectDate: ProjectDate,
  ) {}

  public fetchLeaderBoard = async (): Promise<void> => {
    try {
      if (this.gameStatusStore.isStatusGameMocks()) {
        const [response] = await Promise.all([this.projectApi.fetchLeaderBoard(), this.fetchLeague()]);
        this.leaderBoardStore.setContent(response.payload);
      } else {
        const response = await this.request();
        this.leaderBoardStore.setContent(response.payload);
      }

      this.leaderBoardStore.setSuccessFetchStatus();
    } catch (error) {
      this.loggerService.logHttpError(error as HttpError);

      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      } else {
        this.leaderBoardStore.setErrorFetchStatus();
        this.leaderBoardStore.setErrorFetchData(error);
      }
    }
  };

  private request = throttle(
    async () => {
      const [response] = await Promise.all([this.projectApi.fetchLeaderBoard(), this.fetchLeague()]);

      return response;
    },
    LEADERBOARD_FETCH_THROTTLING_TIME,
    {leading: true, trailing: false},
  );

  public async fetchLeague(): Promise<void> {
    try {
      const response = await this.projectApi.fetchLeague();

      if (LeaderBoardStore.needInitLeague(response.payload)) {
        const responseByInit = await this.projectApi.initLeague();

        this.updateLeagueProps(responseByInit.payload);
      } else {
        this.updateLeagueProps(response.payload);
      }

      this.startUpdateRecalculateLeagueTime();
    } catch (error) {
      this.loggerService.logHttpError(error as HttpError);

      if (this.errorsService.isErrorRecalculationLeagues(error)) {
        this.gameStatusService.setStatusLeaguesRecalculation();
      } else {
        this.leaderBoardStore.setErrorFetchStatus();
        this.leaderBoardStore.setErrorFetchData(error);
      }
    }
  }

  public updateActiveLeagueTab(league: LeagueType): void {
    // Нельзя переключиться на предыдущую лигу
    if (this.leaderBoardStore.isPreviousLeague(league)) {
      return;
    }

    this.leaderBoardStore.setActiveLeagueTab(league);
  }

  private startUpdateRecalculateLeagueTime(): void {
    this.projectDate.unsubscribe(this.handleUpdateTime);
    this.projectDate.subscribe(this.handleUpdateTime);
  }

  private handleUpdateTime = (): void => {
    const leagueRecalculationAt = this.leaderBoardStore.getNextRecalculationAt();

    if (!leagueRecalculationAt) return;

    const leftTime = this.projectDate.getTimeBefore(leagueRecalculationAt);
    const date = FormatUtils.formatLeaderBoardTimer(DateUtils.millisecondsToSeconds(leftTime));
    this.leaderBoardStore.setTimerToRecalculateLeagueValue(date);

    if (leftTime === 0) {
      this.gameStatusService.setStatusLeaguesRecalculation();
      this.navigationClickerModalService.clearCurrentModalType();
    }
  };

  public clearLeaderBoardStatus(): void {
    this.leaderBoardStore.setIdleFetchStatus();
  }

  public clearLeaderBoardErrorData(): void {
    this.leaderBoardStore.clearErrorFetchData();
  }

  private updateLeagueProps = ({league, score, nextRecalculationAt}: LeagueProps): void => {
    this.leaderBoardStore.setPlayerLeague(league);
    this.leaderBoardStore.setPlayerScore(score);
    this.leaderBoardStore.setNextRecalculationAt(nextRecalculationAt);
  };
}

injected(
  LeaderBoardService,
  DI_TOKENS.projectApi,
  DI_TOKENS.leaderBoardStore,
  DI_TOKENS.gameStatusStore,
  DI_TOKENS.loggerService,
  DI_TOKENS.errorsService,
  DI_TOKENS.gameStatusService,
  DI_TOKENS.navigationClickerModalService,
  DI_TOKENS.projectDate,
);
